import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

const SEARCH_DEBOUNCE_MS = 200

// Org-admin bulk enrollment on the Workplace event page. The three mode chips
// are mutually exclusive; roles and named people nest under their mode.
export default class extends Controller {
  static targets = [
    "modal",
    "formPanel",
    "form",
    "mode",
    "everyoneLabel",
    "rolesPanel",
    "role",
    "roleRemove",
    "roleCount",
    "peoplePanel",
    "search",
    "results",
    "selectedList",
    "selectedCount",
    "selectedCountLabel",
    "includeActor",
    "submit",
    "error",
    "confirmation",
    "confirmationTitle",
    "confirmationMeta",
    "confirmationSegment",
    "confirmationNames",
    "confirmationSkipped",
    "browseEvents"
  ]

  static values = {
    membersUrl: String,
    everyoneCount: { type: Number, default: 0 },
    roleSetCounts: { type: Object, default: {} },
    actorId: Number,
    actorEligible: { type: Boolean, default: false },
    actorRoles: { type: Array, default: [] },
    actorName: String
  }

  connect() {
    this.selectedPeople = []
    this.activeResultIndex = -1
    this.reloadOnClose = false
    this.onShown = () => this.focusFirstMode()
    if (this.hasModalTarget) {
      this.modalTarget.addEventListener("shown.bs.modal", this.onShown)
    }
  }

  disconnect() {
    this.clearSearchTimer()
    if (this.hasModalTarget) {
      this.modalTarget.removeEventListener("shown.bs.modal", this.onShown)
    }
  }

  open(event) {
    event.preventDefault()
    if (!this.hasModalTarget) return

    this.resetForm()
    Modal.getOrCreateInstance(this.modalTarget).show()
  }

  modalHidden() {
    if (this.reloadOnClose) {
      window.location.reload()
      return
    }

    this.resetForm()
  }

  closeAfterSuccess(event) {
    event.preventDefault()
    this.reloadOnClose = true
    Modal.getOrCreateInstance(this.modalTarget).hide()
  }

  modeChanged() {
    this.syncPanels()
    this.selectionChanged()
  }

  rolesChanged() {
    this.syncRoleChips()
    this.selectionChanged()
  }

  clearRole(event) {
    event.preventDefault()
    const role = event.currentTarget.dataset.role
    const input = this.roleTargets.find((candidate) => candidate.value === role)
    if (!input) return

    input.checked = false
    this.rolesChanged()
  }

  selectionChanged() {
    this.clearError()
    this.submitTarget.disabled = !this.canSubmit()
  }

  searchInput() {
    this.clearSearchTimer()
    const query = this.searchTarget.value.trim()
    if (query.length < 2) {
      this.hideResults()
      return
    }

    this.searchTimer = window.setTimeout(() => this.fetchMembers(query), SEARCH_DEBOUNCE_MS)
  }

  searchKeydown(event) {
    if (!this.hasResultsTarget || this.resultsTarget.hidden) return

    const items = this.resultItems()
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveResult(1, items)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveResult(-1, items)
    } else if (event.key === "Enter") {
      if (this.activeResultIndex < 0) return
      event.preventDefault()
      items[this.activeResultIndex]?.click()
    } else if (event.key === "Escape") {
      this.hideResults()
    }
  }

  pickMember(event) {
    event.preventDefault()
    const button = event.currentTarget
    const id = Number(button.dataset.id)
    if (!id || this.selectedPeople.some((person) => person.id === id)) {
      this.hideResults()
      return
    }

    this.selectedPeople.push({ id, name: button.dataset.name })
    this.searchTarget.value = ""
    this.hideResults()
    this.renderSelected()
    this.selectionChanged()
    this.searchTarget.focus()
  }

  removePerson(event) {
    event.preventDefault()
    const id = Number(event.currentTarget.dataset.id)
    this.selectedPeople = this.selectedPeople.filter((person) => person.id !== id)
    this.renderSelected()
    this.selectionChanged()
  }

  clearPeople(event) {
    event.preventDefault()
    this.selectedPeople = []
    this.renderSelected()
    this.selectionChanged()
    this.searchTarget.focus()
  }

  async submit(event) {
    event.preventDefault()
    if (!this.canSubmit() || this.submitting) return

    this.submitting = true
    this.submitTarget.disabled = true
    this.clearError()

    let response
    try {
      response = await fetch(this.formTarget.action, {
        method: "POST",
        headers: this.requestHeaders(),
        body: this.requestBody(),
        credentials: "same-origin"
      })
    } catch {
      this.showError("We couldn't register that group. Try again.")
      this.submitting = false
      this.selectionChanged()
      return
    }

    const payload = await response.json().catch(() => ({}))
    this.submitting = false

    if (!response.ok) {
      this.showError(payload.error || "We couldn't register that group.")
      this.selectionChanged()
      return
    }

    this.showConfirmation(payload)
  }

  focusFirstMode() {
    this.modeTargets[0]?.focus()
  }

  resetForm() {
    this.clearSearchTimer()
    this.selectedPeople = []
    this.reloadOnClose = false
    this.submitting = false
    this.modeTargets.forEach((input) => { input.checked = false })
    this.roleTargets.forEach((input) => { input.checked = false })
    if (this.hasIncludeActorTarget) this.includeActorTarget.checked = true
    if (this.hasSearchTarget) this.searchTarget.value = ""
    this.hideResults()
    this.renderSelected()
    this.syncRoleChips()
    this.syncPanels()
    this.clearError()
    this.formPanelTarget.hidden = false
    this.confirmationTarget.hidden = true
    this.submitTarget.disabled = true
    this.submitTarget.textContent = "Register Team"
  }

  syncPanels() {
    const mode = this.selectedMode()
    this.rolesPanelTarget.hidden = mode !== "roles"
    this.peoplePanelTarget.hidden = mode !== "people"
    if (mode !== "people") this.hideResults()
    this.searchTarget.setAttribute("aria-expanded", "false")
  }

  syncRoleChips() {
    this.roleTargets.forEach((input) => {
      const remove = this.roleRemoveTargets.find((button) => button.dataset.role === input.value)
      if (remove) remove.hidden = !input.checked
    })

    const count = this.selectedRoleCount()
    const selected = this.selectedRoles().length > 0
    this.roleCountTarget.hidden = this.selectedMode() !== "roles" || !selected
    this.roleCountTarget.textContent = selected ? `${count} ${count === 1 ? "member" : "members"}` : ""
  }

  renderSelected() {
    const count = this.selectedPeople.length
    this.selectedCountTarget.hidden = count === 0
    this.selectedCountLabelTarget.textContent = `${count} Selected`
    this.selectedListTarget.hidden = count === 0
    this.selectedListTarget.replaceChildren(
      ...this.selectedPeople.map((person) => this.selectedChip(person))
    )
  }

  selectedChip(person) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "hub-register-team-chip is-selected"
    button.dataset.id = String(person.id)
    button.dataset.action = "click->register-team-modal#removePerson"
    button.setAttribute("aria-label", `Remove ${person.name}`)
    button.innerHTML = `<span></span><i class="ph ph-x" aria-hidden="true"></i>`
    button.querySelector("span").textContent = person.name
    return button
  }

  async fetchMembers(query) {
    const url = new URL(this.membersUrlValue, window.location.origin)
    url.searchParams.set("q", query)

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken },
        credentials: "same-origin"
      })
      if (!response.ok) return

      const payload = await response.json()
      this.showResults(payload.members || [])
    } catch {
      this.hideResults()
    }
  }

  showResults(members) {
    const selectedIds = new Set(this.selectedPeople.map((person) => person.id))
    const available = members.filter((member) => !selectedIds.has(member.id))
    this.resultsTarget.replaceChildren(
      ...available.map((member, index) => this.resultItem(member, index))
    )
    this.resultsTarget.hidden = available.length === 0
    this.searchTarget.setAttribute("aria-expanded", available.length > 0 ? "true" : "false")
    this.activeResultIndex = available.length > 0 ? 0 : -1
    this.syncActiveResult()
  }

  resultItem(member, index) {
    const button = document.createElement("button")
    button.type = "button"
    button.id = `register-team-result-${member.id}`
    button.className = "hub-register-team-search__result"
    button.role = "option"
    button.dataset.id = String(member.id)
    button.dataset.name = member.name
    button.dataset.action = "click->register-team-modal#pickMember"
    button.dataset.index = String(index)
    const name = document.createElement("span")
    name.textContent = member.name
    const email = document.createElement("span")
    email.className = "hub-register-team-search__email"
    email.textContent = member.email
    button.append(name, email)
    return button
  }

  hideResults() {
    this.resultsTarget.hidden = true
    this.resultsTarget.replaceChildren()
    this.activeResultIndex = -1
    this.searchTarget.setAttribute("aria-expanded", "false")
    this.searchTarget.removeAttribute("aria-activedescendant")
  }

  resultItems() {
    return Array.from(this.resultsTarget.querySelectorAll("[role='option']"))
  }

  moveResult(delta, items) {
    if (items.length === 0) return

    this.activeResultIndex = (this.activeResultIndex + delta + items.length) % items.length
    this.syncActiveResult()
    items[this.activeResultIndex].scrollIntoView({ block: "nearest" })
  }

  syncActiveResult() {
    const items = this.resultItems()
    items.forEach((item, index) => {
      const active = index === this.activeResultIndex
      item.classList.toggle("is-active", active)
      item.setAttribute("aria-selected", active ? "true" : "false")
    })
    const current = items[this.activeResultIndex]
    if (current) this.searchTarget.setAttribute("aria-activedescendant", current.id)
    else this.searchTarget.removeAttribute("aria-activedescendant")
  }

  selectedMode() {
    return this.modeTargets.find((input) => input.checked)?.value || ""
  }

  selectedRoles() {
    return this.roleTargets.filter((input) => input.checked).map((input) => input.value)
  }

  selectedRoleCount() {
    // Counted by role set, so a member holding two selected roles is one person.
    const selected = this.selectedRoles()
    return Object.entries(this.roleSetCountsValue).reduce((sum, [set, count]) => {
      return set.split(" ").some((role) => selected.includes(role)) ? sum + count : sum
    }, 0)
  }

  includeActor() {
    return this.hasIncludeActorTarget && this.includeActorTarget.checked
  }

  coveredCount() {
    const mode = this.selectedMode()
    if (mode === "everyone") {
      const everyone = this.everyoneCountValue
      if (this.includeActor() || !this.actorEligibleValue) return everyone
      return Math.max(0, everyone - 1)
    }

    if (mode === "roles") {
      const count = this.selectedRoleCount()
      if (!this.includeActor() || !this.actorEligibleValue) return count
      if (this.selectedRoles().some((role) => this.actorRolesValue.includes(role))) return count
      return count + 1
    }

    if (mode === "people") {
      const count = this.selectedPeople.length
      if (!this.includeActor() || !this.actorEligibleValue) return count
      if (this.selectedPeople.some((person) => person.id === this.actorIdValue)) return count
      return count + 1
    }

    return 0
  }

  canSubmit() {
    const mode = this.selectedMode()
    if (mode === "everyone") return this.coveredCount() > 0
    if (mode === "roles") return this.selectedRoles().length > 0
    if (mode === "people") return this.selectedPeople.length > 0
    return false
  }

  requestHeaders() {
    return {
      Accept: "application/json",
      "Content-Type": "application/x-www-form-urlencoded",
      "X-CSRF-Token": this.csrfToken
    }
  }

  requestBody() {
    const body = new URLSearchParams()
    body.set("mode", this.selectedMode())
    body.set("include_actor", this.includeActor() ? "1" : "0")
    this.selectedRoles().forEach((role) => body.append("roles[]", role))
    this.selectedPeople.forEach((person) => body.append("user_ids[]", String(person.id)))
    return body
  }

  showConfirmation(payload) {
    this.reloadOnClose = true
    this.formPanelTarget.hidden = true
    this.confirmationTarget.hidden = false
    this.confirmationTitleTarget.textContent = payload.event_title || ""
    this.confirmationMetaTarget.textContent = this.eventMetaText()
    this.confirmationSegmentTarget.textContent = this.confirmationSegmentText(payload)
    this.confirmationNamesTarget.textContent = this.namesSentence(
      payload.registered_names || [],
      payload.registered_count
    )
    this.confirmationSkippedTarget.textContent = this.skippedText(payload)
    if (payload.events_path) this.browseEventsTarget.href = payload.events_path
    this.confirmationTarget.querySelector("h2")?.focus()
  }

  eventMetaText() {
    const event = this.formPanelTarget.querySelector(".hub-register-team-modal__event-meta")
    return event ? event.textContent.replace(/\s+/g, " ").trim() : ""
  }

  confirmationSegmentText(payload) {
    const count = payload.registered_count || 0
    const segment = payload.segment_label || "Your selection"
    return `${segment} · ${count} ${count === 1 ? "person" : "people"} registered`
  }

  namesSentence(names, count) {
    if (!names.length) return count ? `${count} people were registered.` : "No new people were registered."
    if (names.length <= 8) return `Registered: ${names.join(", ")}.`
    const shown = names.slice(0, 8).join(", ")
    return `Registered: ${shown}, and ${names.length - 8} more.`
  }

  skippedText(payload) {
    const already = payload.already_registered_count || 0
    if (!already) return ""
    return `${already} ${already === 1 ? "person was" : "people were"} already registered and were not notified again.`
  }

  showError(message) {
    this.errorTarget.hidden = false
    this.errorTarget.textContent = message
  }

  clearError() {
    this.errorTarget.hidden = true
    this.errorTarget.textContent = ""
  }

  clearSearchTimer() {
    if (this.searchTimer) window.clearTimeout(this.searchTimer)
    this.searchTimer = null
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
